"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { MoveLeft } from "lucide-react";
import MaskedImage from "@/components/MaskedImage";
import { getImageUrl } from "@/lib/utils/image";
import { getBlogPostHref } from "@/lib/utils/blog";

let Swiper = null;
let SwiperSlide = null;

function PostCard({ post, showImage = false, className = "" }) {
  const href = getBlogPostHref(post);
  const imageSrc = getImageUrl(post?.image || post?.image_url);
  const Wrapper = href ? Link : "div";
  const wrapperProps = href
    ? { href, "aria-label": post?.title || "مطالعه مقاله" }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      className={`glass-box rounded-4xl p-6 flex flex-col justify-between ${className}`}
    >
      <div className="flex justify-between gap-6">
        {showImage ? (
          <div className="w-[232px] h-[290px] object-cover shrink-0">
            <MaskedImage
              src={imageSrc}
              alt={post?.title || ""}
              maskSrc="/image/blogs-mask.svg"
              className="w-full h-full object-cover"
              priority
            />
          </div>
        ) : null}

        <div className="flex flex-col justify-between w-[244px] h-[290px]">
          <div>
            <div className="mb-5 text-lg line-clamp-2">{post?.title}</div>
            <div className="text-mute line-clamp-6">
              {post?.desc || post?.summary || post?.excerpt}
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span>مطالعه بیشتر</span>
            <span className="w-12 h-12 rounded-full border border-white flex items-center justify-center group">
              <MoveLeft size={20} className="text-white" />
            </span>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}

export default function BlogPostsSlider({ posts = [] }) {
  const [swiperLoaded, setSwiperLoaded] = useState(false);

  useEffect(() => {
    Promise.all([import("swiper/react"), import("swiper/css")])
      .then(([swiperModule]) => {
        Swiper = swiperModule.Swiper;
        SwiperSlide = swiperModule.SwiperSlide;
        setSwiperLoaded(true);
      })
      .catch(console.error);
  }, []);

  if (!Array.isArray(posts) || posts.length === 0) return null;

  const featuredPost = posts[0];
  const restPosts = posts.slice(1);
  const featuredHref = getBlogPostHref(featuredPost);
  const featuredImageSrc = getImageUrl(
    featuredPost?.image || featuredPost?.image_url
  );
  const FeaturedWrapper = featuredHref ? Link : "div";
  const featuredProps = featuredHref
    ? { href: featuredHref, "aria-label": featuredPost?.title || "مطالعه مقاله" }
    : {};

  return (
    <div className="w-full overflow-hidden px-4">
      <div className="sm:hidden">
        <FeaturedWrapper
          {...featuredProps}
          className="glass-box rounded-4xl p-6 w-full block"
        >
          <div className="w-full h-[220px] mb-5">
            <MaskedImage
              src={featuredImageSrc}
              alt={featuredPost?.title || ""}
              maskSrc="/image/blogs-mask.svg"
              className="w-full h-full object-cover"
              priority
            />
          </div>

          <div className="mb-4 text-lg line-clamp-2">{featuredPost?.title}</div>
          <div className="text-mute line-clamp-4">
            {featuredPost?.desc ||
              featuredPost?.summary ||
              featuredPost?.excerpt}
          </div>

          <div className="mt-6 flex justify-between items-center">
            <span>مطالعه بیشتر</span>
            <span className="w-12 h-12 rounded-full border border-white flex items-center justify-center">
              <MoveLeft size={20} className="text-white" />
            </span>
          </div>
        </FeaturedWrapper>

        {restPosts.length > 0 && swiperLoaded && Swiper ? (
          <div className="mt-4 overflow-hidden">
            <Swiper
              slidesPerView="auto"
              spaceBetween={16}
              grabCursor
              watchSlidesProgress
              observer
              observeParents
              className="w-full overflow-hidden"
              breakpoints={{ 1024: { spaceBetween: 24 } }}
            >
              {restPosts.map((post) => (
                <SwiperSlide
                  key={post?.slug || post?.id || post?.UUID}
                  className="!w-auto"
                >
                  <PostCard post={post} className="h-[340px]" />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        ) : null}
      </div>

      <div className="hidden sm:block">
        {swiperLoaded && Swiper ? (
          <Swiper
            slidesPerView="auto"
            spaceBetween={16}
            grabCursor
            watchSlidesProgress
            observer
            observeParents
            className="w-full !overflow-visible"
            breakpoints={{ 1024: { spaceBetween: 24 } }}
          >
            {posts.map((post, index) => (
              <SwiperSlide
                key={post?.slug || post?.id || post?.UUID}
                className="!w-auto"
              >
                <PostCard
                  post={post}
                  showImage={index === 0}
                  className="h-[340px]"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {posts.slice(0, 3).map((post, index) => (
              <div
                key={post?.slug || post?.id || post?.UUID}
                className="flex-shrink-0"
              >
                <PostCard
                  post={post}
                  showImage={index === 0}
                  className="h-[340px]"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
